/**
 * Environment Configuration Manager
 *
 * This module manages environment-specific configurations and settings
 * for different modes (Demo, FHIR/Greenfield, ASC) in the application.
 */

// Detect if we're running locally or in production
const isLocal =
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const productionApiUrl = `${window.location.protocol}//${window.location.host}`;

// Default configuration for Production mode
const productionConfig = {
  mode: 'production',
  apiBaseUrl: isLocal ? '' : productionApiUrl,
  patientEndpoint: '/api/patients',
  ordersEndpoint: '/api/orders',
  labsEndpoint: '/api/labs',
  surgeonsEndpoint: '/api/surgeons',
  preopSummaryEndpoint: '/api/patients', // Added for preop summary endpoint
  useLocalStorage: false, // Production mode loads from database
  dataStructure: {
    patientIdField: 'id',
    allergiesField: 'allergies',
    medicationsField: 'medications',
    comorbiditiesField: 'comorbidities',
    labsField: 'labs',
  },
  mockDataEnabled: false, // Production mode uses real data
  mockSurgeons: [], // No mock surgeons in production
};

// Configuration for FHIR/Greenfield mode
const fhirConfig = {
  mode: 'greenfield',
  apiBaseUrl: isLocal ? '' : productionApiUrl, // Dynamic based on environment
  patientEndpoint: '/api/patients',
  ordersEndpoint: '/api/orders',
  labsEndpoint: '/api/labs',
  surgeonsEndpoint: '/api/surgeons',
  useLocalStorage: false,
  dataStructure: {
    patientIdField: 'fhirId',
    allergiesField: 'allergyIntolerance',
    medicationsField: 'medicationStatement',
    comorbiditiesField: 'conditions',
    labsField: 'observations',
  },
  mockDataEnabled: false,
  mockSurgeons: [], // Defaults to empty array
};

// Configuration for ASC mode
const ascConfig = {
  mode: 'asc',
  // Use the isLocal variable defined above
  apiBaseUrl: isLocal ? '' : productionApiUrl,
  patientEndpoint: '/api/asc/patients',
  ordersEndpoint: '/api/asc/orders',
  labsEndpoint: '/api/asc/labs',
  surgeonsEndpoint: '/api/asc/surgeons',
  protocolsEndpoint: '/api/asc/protocols',
  facilitiesEndpoint: '/api/asc/facilities',
  surgeriesEndpoint: '/api/asc/surgeries',
  authEndpoints: {
    login: '/api/asc/auth/login', // Corrected path
    logout: '/api/asc/auth/logout', // Assuming consistent path structure
    me: '/api/asc/auth/me', // Assuming consistent path structure
  },
  useLocalStorage: false,
  dataStructure: {
    patientIdField: 'id',
    allergiesField: 'allergies',
    medicationsField: 'medications',
    comorbiditiesField: 'conditions',
    labsField: 'labs',
  },
  mockDataEnabled: false,
  mockSurgeons: [], // Defaults to empty array
};

// Configuration for Hospital mode
const hospitalConfig = {
  mode: 'hospital',
  apiBaseUrl: isLocal ? '' : productionApiUrl,
  patientEndpoint: '/api/patients',
  ordersEndpoint: '/api/orders',
  labsEndpoint: '/api/labs',
  surgeonsEndpoint: '/api/surgeons',
  protocolsEndpoint: '/api/protocols',
  facilitiesEndpoint: '/api/facilities',
  surgeriesEndpoint: '/api/surgeries',
  authEndpoints: {
    login: '/api/auth/login',
    logout: '/api/auth/logout',
    me: '/api/auth/me',
  },
  useLocalStorage: false,
  dataStructure: {
    patientIdField: 'id',
    allergiesField: 'allergies',
    medicationsField: 'medications',
    comorbiditiesField: 'comorbidities',
    labsField: 'labs',
  },
  mockDataEnabled: false,
  mockSurgeons: [],
};

// Active configuration (defaults to production)
let activeConfig = { ...productionConfig };

/**
 * Sets the active environment mode
 * @param {string} mode - 'production', 'greenfield', 'asc', or 'hospital'
 * @returns {Object} The active configuration
 */
export function setEnvironmentMode(mode) {
  // If the mode is already set to the requested mode, don't do anything
  if (activeConfig.mode === mode) {
    return activeConfig;
  }

  if (mode === 'greenfield' || mode === 'fhir') {
    activeConfig = { ...fhirConfig };
  } else if (mode === 'asc') {
    activeConfig = { ...ascConfig };
  } else if (mode === 'hospital') {
    activeConfig = { ...hospitalConfig };
  } else {
    activeConfig = { ...productionConfig };
  }

  // Store the current mode in localStorage for persistence
  localStorage.setItem('environmentMode', activeConfig.mode);
  // Only update appMode if it's not already set correctly
  const currentAppMode = localStorage.getItem('appMode');
  if (mode === 'asc') {
    localStorage.setItem('appMode', 'asc');
  } else if (mode === 'hospital' && currentAppMode !== 'hospital') {
    localStorage.setItem('appMode', 'hospital');
  }

  console.log(
    `[EnvironmentConfig] Mode set to: ${activeConfig.mode}, API Base URL: ${activeConfig.apiBaseUrl}`
  );
  return activeConfig;
}

/**
 * Gets the current active configuration
 * @returns {Object} The active configuration
 */
export function getConfig() {
  return activeConfig;
}

/**
 * Gets the appropriate API endpoint for the current mode
 * @param {string} endpointType - Type of endpoint (e.g., 'patient', 'orders')
 * @returns {string} The full endpoint URL
 */
export function getApiEndpoint(endpointType) {
  // Get the current mode without changing it
  const currentMode = activeConfig.mode;

  // For production mode, always use the production endpoints
  if (currentMode === 'production') {
    const productionEndpoints = {
      patients: `${productionConfig.apiBaseUrl}${productionConfig.patientEndpoint}`,
      orders: `${productionConfig.apiBaseUrl}${productionConfig.ordersEndpoint}`,
      labs: `${productionConfig.apiBaseUrl}${productionConfig.labsEndpoint}`,
      surgeons: `${productionConfig.apiBaseUrl}${productionConfig.surgeonsEndpoint}`,
      default: `${productionConfig.apiBaseUrl}/${endpointType}`,
    };

    const endpoint = productionEndpoints[endpointType] || productionEndpoints.default;
    // Use debug level logging to reduce console noise
    console.debug(`[EnvironmentConfig] Using PRODUCTION endpoint for ${endpointType}: ${endpoint}`);
    return endpoint;
  }

  // For ASC mode, use the ASC endpoints
  if (currentMode === 'asc') {
    const ascEndpoints = {
      patients: `${ascConfig.apiBaseUrl}${ascConfig.patientEndpoint}`,
      orders: `${ascConfig.apiBaseUrl}${ascConfig.ordersEndpoint}`,
      labs: `${ascConfig.apiBaseUrl}${ascConfig.labsEndpoint}`,
      surgeons: `${ascConfig.apiBaseUrl}${ascConfig.surgeonsEndpoint}`,
      protocols: `${ascConfig.apiBaseUrl}${ascConfig.protocolsEndpoint}`,
      facilities: `${ascConfig.apiBaseUrl}${ascConfig.facilitiesEndpoint}`,
      surgeries: `${ascConfig.apiBaseUrl}${ascConfig.surgeriesEndpoint}`,
      default: `${ascConfig.apiBaseUrl}/api/asc/${endpointType}`,
    };

    const endpoint = ascEndpoints[endpointType] || ascEndpoints.default;
    console.debug(`[EnvironmentConfig] Using ASC endpoint for ${endpointType}: ${endpoint}`);
    return endpoint;
  }

  // For greenfield mode, use the configured endpoints
  const endpoint = (() => {
    switch (endpointType) {
      case 'patients':
        return `${activeConfig.apiBaseUrl}${activeConfig.patientEndpoint}`;
      case 'orders':
        return `${activeConfig.apiBaseUrl}${activeConfig.ordersEndpoint}`;
      case 'labs':
        return `${activeConfig.apiBaseUrl}${activeConfig.labsEndpoint}`;
      case 'surgeons':
        return `${activeConfig.apiBaseUrl}${activeConfig.surgeonsEndpoint || '/surgeons'}`; // Use defined or default
      default:
        return `${activeConfig.apiBaseUrl}/${endpointType}`;
    }
  })();

  // Use debug level logging to reduce console noise
  console.debug(`[EnvironmentConfig] Using GREENFIELD endpoint for ${endpointType}: ${endpoint}`);
  return endpoint;
}

/**
 * Gets the appropriate field name for the current mode
 * @param {string} fieldType - Type of field (e.g., 'allergies', 'medications')
 * @returns {string} The field name for the current mode
 */
export function getFieldName(fieldType) {
  return activeConfig.dataStructure[`${fieldType}Field`] || fieldType;
}

/**
 * Initialize the environment configuration
 * Tries to restore the previously used mode from localStorage
 */
export function initEnvironment() {
  // Check for app mode first (supports both ASC and Hospital)
  const appMode = localStorage.getItem('appMode');
  if (appMode === 'asc') {
    setEnvironmentMode('asc');
    return activeConfig;
  } else if (appMode === 'hospital') {
    setEnvironmentMode('hospital');
    return activeConfig;
  }

  // Otherwise use the saved environment mode
  const savedMode = localStorage.getItem('environmentMode');
  if (savedMode) {
    setEnvironmentMode(savedMode);
  } else {
    // No saved mode - determine default based on environment
    // On production (virtualpat.ai), default to 'hospital' mode for enterprise
    const isProduction =
      window.location.hostname === 'virtualpat.ai' ||
      window.location.hostname.includes('virtualpat');

    if (isProduction) {
      console.log('[EnvironmentConfig] Production detected, defaulting to hospital mode');
      setEnvironmentMode('hospital');
    } else {
      // Local development defaults to production mode
      console.log('[EnvironmentConfig] Local environment detected, defaulting to production mode');
      setEnvironmentMode('production');
    }
  }
  return activeConfig;
}

// Initialize on import
initEnvironment();

export default {
  getConfig,
  setEnvironmentMode,
  getApiEndpoint,
  getFieldName,
  initEnvironment,
};
